import { createContext, ElementType, memo, useEffect, useRef } from 'react'
import { Box } from '@stoplight/mosaic'

import { isDivider, getHtmlIdFromItemId, TableOfContentsItem } from '.'
import { TableOfContentsGroupItem } from './TableOfContentsGroupItem'

type TableOfContentsProps<T = ElementType> = {
  items: TableOfContentsItem[]
  activeId: string
  Link: T
  maxDepthOpenByDefault?: number
  externalScrollbar?: boolean
  onLinkClick?: () => void
}

export const ActiveIdContext = createContext<string | undefined>(undefined)
export const LinkContext = createContext<ElementType | undefined>(undefined)

export const TableOfContents = memo<TableOfContentsProps>(
  ({
    items,
    activeId,
    Link,
    maxDepthOpenByDefault,
    externalScrollbar = false,
    onLinkClick,
  }) => {
    const container = useRef<HTMLDivElement>(null)
    const child = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const tocHasScrollbar =
        externalScrollbar ||
        (container.current &&
          child.current &&
          container.current.offsetHeight < child.current.offsetHeight)

      if (activeId && typeof window !== 'undefined' && tocHasScrollbar) {
        const elem = window.document.getElementById(
          getHtmlIdFromItemId(activeId)
        )
        if (elem && 'scrollIntoView' in elem) {
          elem.scrollIntoView({ block: 'center' })
        }
      }

      // Only want to run this effect on initial render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
      <Box ref={container} w="full" bg="transparent" overflowY="auto">
        <Box ref={child} my={3}>
          <LinkContext.Provider value={Link}>
            <ActiveIdContext.Provider value={activeId}>
              {items.map((item, key) => {
                if (isDivider(item)) {
                  return null
                }

                return (
                  <TableOfContentsGroupItem
                    key={key}
                    item={item}
                    depth={0}
                    maxDepthOpenByDefault={maxDepthOpenByDefault}
                    onLinkClick={onLinkClick}
                  />
                )
              })}
            </ActiveIdContext.Provider>
          </LinkContext.Provider>
        </Box>
      </Box>
    )
  }
)
